import { z } from "zod";
export const edgesSchema = z
  .object({
    top: z.number().min(0).max(14400),
    right: z.number().min(0).max(14400),
    bottom: z.number().min(0).max(14400),
    left: z.number().min(0).max(14400),
  })
  .strict();
export type Edges = z.infer<typeof edgesSchema>;
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}
/** Custom artwork bounds use points relative to the top-left of finished trim. */
export const pdfArtBoxSchema = z.union([
  z.enum(["media", "trim", "bleed"]),
  z
    .object({
      x: z.number().min(-14400).max(14400),
      y: z.number().min(-14400).max(14400),
      width: z.number().positive().max(14400),
      height: z.number().positive().max(14400),
    })
    .strict(),
]);
export type PdfArtBox = z.infer<typeof pdfArtBoxSchema>;
export function resolvePdfArtBox(
  choice: PdfArtBox | undefined,
  page: { width: number; height: number; trim: Rect; bleed?: Rect },
): Rect | undefined {
  if (choice === undefined) return;
  const media = { x: 0, y: 0, width: page.width, height: page.height };
  const box =
    choice === "media"
      ? media
      : choice === "trim"
        ? page.trim
        : choice === "bleed"
          ? (page.bleed ?? page.trim)
          : { ...choice, x: page.trim.x + choice.x, y: page.trim.y + choice.y };
  if (
    !Object.values(box).every(Number.isFinite) ||
    box.width <= 0 ||
    box.height <= 0 ||
    box.x < 0 ||
    box.y < 0 ||
    box.x + box.width > media.width ||
    box.y + box.height > media.height
  )
    throw new Error(
      "PDF artwork bounds must fit inside the exported media box",
    );
  return { ...box };
}
export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
export const edges = (value: number | Edges): Edges =>
  typeof value === "number"
    ? { top: value, right: value, bottom: value, left: value }
    : { ...value };
export const rotateEdges = (e: Edges): Edges => ({
  top: e.left,
  right: e.top,
  bottom: e.right,
  left: e.bottom,
});
const productionEdges = z.union([
  z.number().min(0).max(144),
  edgesSchema.refine(
    (e) => Object.values(e).every((n) => n <= 144),
    "Production edges exceed 144 points",
  ),
]);
export const markSettingsSchema = z.object({
  length: z.number().min(1).max(144).default(9),
  offset: z.number().min(0).max(144).default(3),
  strokeWidth: z.number().min(0.1).max(2).default(0.5),
  reference: z.enum(["bleed", "trim"]).optional(),
  outlineWidth: z.number().min(0).max(4).optional(),
});
export const productionSchema = z.object({
  combineCropMarks: z.boolean().optional(),
  combineBleedMarks: z.boolean().optional(),
  clipArtworkToBleed: z.boolean().optional(),
  bleed: productionEdges.default(0),
  slug: productionEdges.default(0),
  cropMarks: z.boolean().default(false),
  markSettings: markSettingsSchema.optional(),
  bleedMarks: z.boolean().optional(),
  bleedMarkSettings: markSettingsSchema.optional(),
});
export type Production = z.infer<typeof productionSchema>;
export function cropSegments(
  trim: Rect,
  bleed: Rect,
  length: number,
  offset: number,
): Segment[] {
  const l = trim.x,
    r = l + trim.width,
    t = trim.y,
    b = t + trim.height,
    bl = bleed.x,
    br = bl + bleed.width,
    bt = bleed.y,
    bb = bt + bleed.height;
  return [
    { x1: bl - offset, y1: t, x2: bl - offset - length, y2: t },
    { x1: br + offset, y1: t, x2: br + offset + length, y2: t },
    { x1: bl - offset, y1: b, x2: bl - offset - length, y2: b },
    { x1: br + offset, y1: b, x2: br + offset + length, y2: b },
    { x1: l, y1: bt - offset, x2: l, y2: bt - offset - length },
    { x1: r, y1: bt - offset, x2: r, y2: bt - offset - length },
    { x1: l, y1: bb + offset, x2: l, y2: bb + offset + length },
    { x1: r, y1: bb + offset, x2: r, y2: bb + offset + length },
  ];
}
export function productionGeometry(
  width: number,
  height: number,
  production: Production,
) {
  const bleed = edges(production.bleed),
    slug = edges(production.slug),
    marks = production.markSettings || {
      length: 8,
      offset: 4,
      strokeWidth: 0.5,
    },
    cropMarkSpace = production.cropMarks
      ? production.markSettings
        ? marks.length + marks.offset
        : 18
      : 0,
    bleedSettings = production.bleedMarkSettings || {
      length: 9,
      offset: 3,
      strokeWidth: 0.5,
    },
    bleedMarkSpace = production.bleedMarks
      ? bleedSettings.length + bleedSettings.offset
      : 0,
    markSpace = Object.fromEntries(
      (["top", "right", "bottom", "left"] as const).map((edge) => [
        edge,
        Math.max(
          bleedMarkSpace,
          Math.max(
            0,
            cropMarkSpace -
              (production.markSettings?.reference === "trim" ? bleed[edge] : 0),
          ),
        ),
      ]),
    ) as unknown as Edges;
  const trim: Rect = {
      x: bleed.left + slug.left + markSpace.left,
      y: bleed.top + slug.top + markSpace.top,
      width,
      height,
    },
    bleedBox: Rect = {
      x: trim.x - bleed.left,
      y: trim.y - bleed.top,
      width: width + bleed.left + bleed.right,
      height: height + bleed.top + bleed.bottom,
    },
    media = {
      width:
        width +
        bleed.left +
        bleed.right +
        slug.left +
        slug.right +
        markSpace.left +
        markSpace.right,
      height:
        height +
        bleed.top +
        bleed.bottom +
        slug.top +
        slug.bottom +
        markSpace.top +
        markSpace.bottom,
    };
  if (media.width > 14400 || media.height > 14400)
    throw new Error("Production media exceeds 14,400 points");
  const cropMarks = production.cropMarks
      ? cropSegments(
          trim,
          production.markSettings?.reference === "trim" ? trim : bleedBox,
          marks.length,
          marks.offset,
        )
      : [],
    bleedMarks = production.bleedMarks
      ? cropSegments(
          bleedBox,
          bleedBox,
          bleedSettings.length,
          bleedSettings.offset,
        )
      : [];
  return {
    ...media,
    trim,
    bleed: bleedBox,
    marks: [...cropMarks, ...bleedMarks],
    markStyles: [
      ...cropMarks.map(() => ({
        strokeWidth: marks.strokeWidth,
        outlineWidth: production.markSettings?.outlineWidth || 0,
      })),
      ...bleedMarks.map(() => ({
        strokeWidth: bleedSettings.strokeWidth,
        outlineWidth: production.bleedMarkSettings?.outlineWidth || 0,
      })),
    ],
    markGroups: [
      {
        kind: "crop",
        segments: cropMarks,
        combined: !!production.combineCropMarks,
        strokeWidth: marks.strokeWidth,
        outlineWidth: production.markSettings?.outlineWidth || 0,
      },
      {
        kind: "bleed",
        segments: bleedMarks,
        combined: !!production.combineBleedMarks,
        strokeWidth: bleedSettings.strokeWidth,
        outlineWidth: production.bleedMarkSettings?.outlineWidth || 0,
      },
    ],
    strokeWidth: marks.strokeWidth,
  };
}
export function intersectRects(a: Rect, b: Rect): Rect {
  const x = Math.max(a.x, b.x),
    y = Math.max(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(0, Math.min(a.x + a.width, b.x + b.width) - x),
    height: Math.max(0, Math.min(a.y + a.height, b.y + b.height) - y),
  };
}
